import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { HugeiconsIcon } from "@hugeicons/react";
import { PlayIcon, PauseIcon, VolumeUpIcon, VolumeMute01Icon } from "@hugeicons/core-free-icons";

interface VideoPlayerProps {
  src: string;
  poster?: string;
  className?: string;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
}

export function VideoPlayer({
  src,
  poster,
  className = "",
  autoplay = true,
  loop = true,
  muted = true,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const isInView = useInView(containerRef, { once: false, margin: "-50px" });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setIsLoaded(true);
      if (autoplay && isInView) {
        video.play().catch(() => {
          // Autoplay failed, user interaction required
        });
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, [autoplay, isInView]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isInView && autoplay && isLoaded) {
      video.play().catch(() => {
        // Autoplay failed
      });
    } else if (!isInView && autoplay) {
      video.pause();
    }
  }, [isInView, autoplay, isLoaded]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  return (
    <div
      ref={containerRef}
      className={`relative group ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="w-full relative aspect-[16/9] rounded-2xl sm:rounded-3xl overflow-hidden bg-gray-900 border border-white/10">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          loop={loop}
          muted={isMuted}
          playsInline
          className="w-full h-full object-cover"
          preload="metadata"
        />

        {/* Loading overlay */}
        {!isLoaded && (
          <div className="absolute inset-0 bg-gray-900/80 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {/* Controls overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered || !isPlaying ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 bg-black/40 flex items-center justify-center"
        >
          {/* Play/Pause button */}
          <motion.button
            onClick={togglePlay}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center hover:bg-white/30 transition-all touch-manipulation"
            style={{ touchAction: 'manipulation' }}
          >
            {isPlaying ? (
              <HugeiconsIcon icon={PauseIcon} size={24} className="sm:w-8 sm:h-8 text-white ml-1" />
            ) : (
              <HugeiconsIcon icon={PlayIcon} size={24} className="sm:w-8 sm:h-8 text-white ml-1" />
            )}
          </motion.button>

          {/* Mute button */}
          <motion.button
            onClick={toggleMute}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center hover:bg-white/30 transition-all touch-manipulation"
            style={{ touchAction: 'manipulation' }}
          >
            {isMuted ? (
              <HugeiconsIcon icon={VolumeMute01Icon} size={16} className="sm:w-5 sm:h-5 text-white" />
            ) : (
              <HugeiconsIcon icon={VolumeUpIcon} size={16} className="sm:w-5 sm:h-5 text-white" />
            )}
          </motion.button>
        </motion.div>

        {/* Subtle glow effect */}
        <div className="absolute inset-0 rounded-2xl sm:rounded-3xl pointer-events-none ring-1 ring-white/5" />
      </div>
    </div>
  );
}
